import React, { useState, useEffect } from 'react';
import { Tabs, Row, Col, Collapse, Button, List, Typography, Space } from 'antd';
import { DownloadOutlined, FilePdfOutlined, FileWordOutlined, FileTextOutlined, FileExcelOutlined } from '@ant-design/icons';
import { useParams, Link } from 'react-router-dom';
import { publicProductService, publicQuoteService } from '../services/publicApi';
import SmartImage from '../components/SmartImage';
import './ProductDetail.css';

const ProductDetail = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    quantity: 1,
    message: '',
    urgency: 'medium',
    preferredContactMethod: 'email'
  });
  const [isSubmittingQuote, setIsSubmittingQuote] = useState(false);
  const [quoteSuccess, setQuoteSuccess] = useState(false);


  const normalizeFileUrl = (raw) => {
    if (!raw) return '';
    let url = String(raw).replace(/\\/g, '/');
    // If absolute http, keep
    if (/^https?:\/\//i.test(url)) return url;
    // Strip any host prefix before /uploads
    const idx = url.indexOf('/uploads/');
    if (idx !== -1) {
      url = url.slice(idx);
    }
    // If path contains 'uploads/' without leading slash
    const idx2 = url.toLowerCase().indexOf('uploads/');
    if (!url.startsWith('/uploads/') && idx2 !== -1) {
      url = '/' + url.slice(idx2);
    }
    // Default documents folder if only filename provided
    if (!url.startsWith('/uploads')) {
      const filename = url.split('/').pop();
      url = `/uploads/documents/${filename}`;
    }
    if (!url.startsWith('/')) url = `/${url}`;
    return `${url}`;
  };

  const getFileIcon = (filename = '') => {
    const lower = filename.toLowerCase();
    if (lower.endsWith('.pdf')) return <FilePdfOutlined style={{ color: '#cf1322' }} />;
    if (lower.endsWith('.doc') || lower.endsWith('.docx')) return <FileWordOutlined style={{ color: '#2b579a' }} />;
    if (lower.endsWith('.xls') || lower.endsWith('.xlsx') || lower.endsWith('.csv')) return <FileExcelOutlined style={{ color: '#237804' }} />;
    return <FileTextOutlined />;
  };

  const getDocumentEntries = (docs, productRef) => {
    if (!docs && !productRef) return [];
    const labelMap = {
      datasheet: 'Fiche technique',
      manual: "Manuel d'utilisation",
      certificate: 'Certificat',
      brochure: 'Brochure',
    };

    const entries = [];

    // Alternate top-level properties on product
    if (productRef && typeof productRef === 'object') {
      ['datasheet', 'manual', 'certificate', 'brochure', 'pdf']
        .forEach((key) => {
          const val = productRef[key];
          if (!val) return;
          const url = typeof val === 'object' ? (val.url || val.path || val.href || val.filename) : val;
          if (!url) return;
          entries.push({
            key: `top_${key}`,
            label: labelMap[key] || key,
            href: normalizeFileUrl(url),
            filename: (typeof val === 'object' ? (val.filename || val.name) : String(url).split('/').pop())
          });
        });
    }

    // Case 1: docs is an array of files
    if (Array.isArray(docs)) {
      docs.forEach((d, idx) => {
        const url = d?.url || d?.href || d?.path || d;
        if (!url) return;
        entries.push({
          key: d?.key || d?.type || `doc_${idx}`,
          label: d?.label || d?.name || d?.filename || `Document ${idx + 1}`,
          href: normalizeFileUrl(url),
          filename: (d?.filename || String(url).split('/').pop()),
        });
      });
      return entries;
    }

    // Case 2: docs is an object with known keys or nested objects
    if (docs && typeof docs === 'object') {
      Object.entries(docs).forEach(([key, value]) => {
        if (!value) return;
        let url = value;
        let filename;
        if (typeof value === 'object') {
          url = value.url || value.href || value.path || value.filename || '';
          filename = value.filename || value.name || String(url).split('/').pop();
        } else {
          filename = String(value).split('/').pop();
        }
        if (!url) return;
        entries.push({
          key,
          label: labelMap[key] || key,
          href: normalizeFileUrl(url),
          filename,
        });
      });
      return entries;
    }

    return [];
  };

  const scanForAnyDocuments = (obj) => {
    const results = [];
    const seen = new Set();
    const add = (label, url) => {
      const href = normalizeFileUrl(url);
      if (seen.has(href)) return;
      seen.add(href);
      results.push({ key: label, label, href, filename: String(url).split('/').pop() });
    };
    const isDoc = (str) => typeof str === 'string' && /(\.pdf|\.docx?|\.xlsx?|\.xls|\.csv)$/i.test(str);
    const walk = (node, path = '') => {
      if (!node) return;
      if (typeof node === 'string') {
        if (isDoc(node)) add(path || 'Document', node);
        return;
      }
      if (Array.isArray(node)) {
        node.forEach((v, i) => walk(v, `${path}[${i}]`));
        return;
      }
      if (typeof node === 'object') {
        Object.entries(node).forEach(([k, v]) => {
          if (typeof v === 'string' && isDoc(v)) add(k, v);
          else if (typeof v === 'object') {
            const url = v?.url || v?.path || v?.href || v?.filename;
            if (url && isDoc(url)) add(k, url);
            walk(v, k);
          }
        });
      }
    };
    walk(obj);
    return results;
  };

  useEffect(() => {
    loadProduct();
  }, [slug]);

  const loadProduct = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await publicProductService.getProductBySlug(slug);
      let p = { ...response.product };
      // Front fallback: si certains champs arrivent stringifiés, tenter de parser
      const tryParse = (val) => {
        if (typeof val !== 'string') return val;
        if (val.trim().startsWith('{') || val.trim().startsWith('[')) {
          try { return JSON.parse(val); } catch { return null; }
        }
        // valeur littérale "[object Object]" => considérer comme vide
        if (val.trim() === '[object Object]') return null;
        return val;
      };
      p.documents = tryParse(p.documents);
      p.pricing = tryParse(p.pricing);
      p.stock = tryParse(p.stock);
      p.technicalSpecs = tryParse(p.technicalSpecs);

      console.log('ProductDetail loaded product:', p);
      console.log('Product images:', p.images);
      console.log('Product main image URL:', p.images?.main);
      if (typeof window !== 'undefined') {
        window.__PRODUCT_DETAIL__ = p;
      }
      // Parser images si c'est un string JSON
      if (typeof p.images === 'string') {
        try { p.images = JSON.parse(p.images); } catch(_) { p.images = { main: null, gallery: [] }; }
      }
      setProduct(p);
    } catch (error) {
      console.error('Erreur lors du chargement du produit:', error);
      setError('Produit non trouvé');
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuoteFormChange = (e) => {
    const { name, value } = e.target;
    setQuoteForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingQuote(true);

    try {
      await publicQuoteService.createQuoteRequest({
        product: product._id,
        customer: {
          firstName: quoteForm.firstName,
          lastName: quoteForm.lastName,
          email: quoteForm.email,
          phone: quoteForm.phone,
          company: quoteForm.company
        },
        requestDetails: {
          quantity: parseInt(quoteForm.quantity),
          message: quoteForm.message,
          urgency: quoteForm.urgency,
          preferredContactMethod: quoteForm.preferredContactMethod
        }
      });

      setQuoteSuccess(true);
      setShowQuoteForm(false);
      setQuoteForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        quantity: 1,
        message: '',
        urgency: 'medium',
        preferredContactMethod: 'email'
      });
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la demande:', error);
      alert('Erreur lors de l\'envoi de la demande de devis');
    } finally {
      setIsSubmittingQuote(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const getCategoryName = (category) => {
    return category ? category.name : '';
  };

  if (isLoading) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner">
              <i className="fas fa-spinner fa-spin"></i>
              <p>Chargement du produit...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <div className="error-container">
            <i className="fas fa-exclamation-triangle"></i>
            <h2>Produit non trouvé</h2>
            <p>Le produit que vous recherchez n'existe pas ou n'est plus disponible.</p>
            <Link to="/produits" className="btn btn--primary">
              <i className="fas fa-arrow-left"></i>
              Retour aux produits
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail-page">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <Link to="/">Accueil</Link>
          <span>/</span>
          <Link to="/produits">Produits</Link>
          <span>/</span>
          <span>{product.name}</span>
        </nav>

        {/* Image principale en haut */}
        <div className="product-gallery" style={{ marginBottom: 24 }}>
          <div className="main-image">
            {product.images?.main ? (
              <SmartImage
                src={product.images.main}
                alt={product.name}
                style={{ width: '100%', maxHeight: 420, objectFit: 'contain' }}
                fallbackIcon="fas fa-image"
              />
            ) : (
              <div className="no-image">
                <i className="fas fa-image"></i>
                <p>Aucune image disponible</p>
              </div>
            )}
          </div>

          {product.images?.gallery && product.images.gallery.length > 0 && (
            <div className="gallery-thumbnails">
              {product.images.gallery.map((image, index) => (
                <div key={index} className="thumbnail">
                  <SmartImage
                    src={image}
                    alt={`${product.name} - Image ${index + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    fallbackIcon="fas fa-image"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Onglets contenu sous l'image (API items AntD) */}
        <Tabs
          defaultActiveKey="details"
          items={[
            {
              key: 'details',
              label: 'Détails du produit',
              children: (
                <Row gutter={[24, 24]}>
               
                  <Col xs={24} md={14}>
                    <div className="product-header" style={{ marginBottom: 16 }}>
                      <div className="product-category">
                        {getCategoryName(product.categories?.[0])}
                      </div>
                      <h1 className="product-title" style={{ marginTop: 8 }}>{product.name}</h1>
                      <div className="product-reference">Référence: <strong>{product.reference}</strong></div>
                    </div>
                    <div className="product-description">
                      <div className="description-content">{product.description}</div>
                    </div>
                  </Col>
                </Row>
              ),
            },
            {
              key: 'documents',
              label: 'Documents techniques',
              children: (
                (
                  getDocumentEntries(product.documents, product).length > 0
                    ? getDocumentEntries(product.documents, product)
                    : scanForAnyDocuments(product)
                ).length > 0 ? (
                  <List
                    itemLayout="horizontal"
                    dataSource={
                      getDocumentEntries(product.documents, product).length > 0
                        ? getDocumentEntries(product.documents, product)
                        : scanForAnyDocuments(product)
                    }
                    renderItem={(item) => (
                      <List.Item
                        actions={[
                          <a key="download" href={item.href} download>
                            <Space>
                              <DownloadOutlined />
                              Télécharger
                            </Space>
                          </a>
                        ]}
                      >
                        <List.Item.Meta
                          avatar={getFileIcon(item.filename)}
                          title={<a href={item.href} target="_blank" rel="noopener noreferrer">{item.label}</a>}
                          description={<Typography.Text type="secondary">{item.filename}</Typography.Text>}
                        />
                      </List.Item>
                    )}
                  />
                ) : (
                  <div style={{ color: '#999' }}>Aucun document disponible</div>
                )
              ),
            },
            {
              key: 'specs',
              label: 'Spécificités techniques',
              children: (() => {
                const normalizeSpecs = (raw) => {
                  if (!raw) return [];
                  let data = raw;
                  if (typeof data === 'string') {
                    try { data = JSON.parse(data); } catch { return []; }
                  }
                  const rows = [];
                  const pushRow = (label, value) => {
                    if (value == null) return;
                    const v = Array.isArray(value)
                      ? value.filter(Boolean).join(', ')
                      : (typeof value === 'object' ? JSON.stringify(value) : String(value));
                    if (!v || !v.trim()) return;
                    rows.push({ label: String(label).trim(), value: v.trim() });
                  };

                  if (Array.isArray(data)) {
                    data.forEach((item) => {
                      if (!item) return;
                      if (typeof item === 'string') {
                        const parts = item.split(':');
                        if (parts.length >= 2) {
                          pushRow(parts[0], parts.slice(1).join(':'));
                        } else {
                          pushRow('Caractéristique', item);
                        }
                      } else if (typeof item === 'object') {
                        const label = item.label || item.name || item.key || item.title;
                        const val = item.value != null ? item.value : item.val;
                        if (label && val != null) {
                          pushRow(label, val);
                        } else {
                          Object.entries(item).forEach(([k, v]) => pushRow(k, v));
                        }
                      }
                    });
                  } else if (typeof data === 'object') {
                    Object.entries(data).forEach(([k, v]) => pushRow(k, v));
                  }
                  return rows;
                };

                const rows = normalizeSpecs(product.technicalSpecs);
                if (rows.length === 0) {
                  return <div style={{ color: '#999' }}>Aucune spécification</div>;
                }
                return (
                  <div className="specs-table">
                    {rows.map((row, idx) => (
                      <div key={`${row.label}-${idx}`} className="spec-row">
                        <span className="spec-label">{row.label}</span>
                        <span className="spec-value">{row.value}</span>
                      </div>
                    ))}
                  </div>
                );
              })(),
            },
         
          ]}
        />

        {/* Formulaire de demande de devis */}
        {showQuoteForm && (
          <div className="quote-modal-overlay" onClick={() => setShowQuoteForm(false)}>
            <div className="quote-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Demande de devis</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowQuoteForm(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="modal-content">
                <div className="quote-product-info">
                  <h3>{product.name}</h3>
                  <p>Référence: {product.reference}</p>
                </div>

                <form onSubmit={handleQuoteSubmit} className="quote-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Prénom *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={quoteForm.firstName}
                        onChange={handleQuoteFormChange}
                        required
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Nom *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={quoteForm.lastName}
                        onChange={handleQuoteFormChange}
                        required
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={quoteForm.email}
                        onChange={handleQuoteFormChange}
                        required
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Téléphone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={quoteForm.phone}
                        onChange={handleQuoteFormChange}
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Entreprise</label>
                    <input
                      type="text"
                      name="company"
                      value={quoteForm.company}
                      onChange={handleQuoteFormChange}
                      className="form-input"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Quantité *</label>
                      <input
                        type="number"
                        name="quantity"
                        value={quoteForm.quantity}
                        onChange={handleQuoteFormChange}
                        min="1"
                        required
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Urgence</label>
                      <select
                        name="urgency"
                        value={quoteForm.urgency}
                        onChange={handleQuoteFormChange}
                        className="form-input"
                      >
                        <option value="low">Faible</option>
                        <option value="medium">Moyenne</option>
                        <option value="high">Élevée</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Message</label>
                    <textarea
                      name="message"
                      value={quoteForm.message}
                      onChange={handleQuoteFormChange}
                      rows="4"
                      className="form-textarea"
                      placeholder="Décrivez vos besoins spécifiques..."
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={() => setShowQuoteForm(false)}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="btn btn--primary"
                      disabled={isSubmittingQuote}
                    >
                      {isSubmittingQuote ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane"></i>
                          Envoyer la demande
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Message de succès */}
        {quoteSuccess && (
          <div className="success-message">
            <i className="fas fa-check-circle"></i>
            <h3>Demande envoyée avec succès !</h3>
            <p>Nous vous contacterons dans les plus brefs délais.</p>
            <button
              className="btn btn--primary"
              onClick={() => setQuoteSuccess(false)}
            >
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
